import type {
  CreatePaymentRequest,
  OpenBill,
  PaymentAutoAllocatePreview,
  PaymentAutoAllocatePreviewRequest,
  PaymentResponse,
  PurchaseReturnResponse,
  SetPaymentAllocationsRequest,
  UpdatePaymentRequest,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE, PICKER_PAGE_SIZE } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { BILLS_KEY, PURCHASE_ORDERS_KEY } from '@/features/purchases/api';
import { throwApiError } from '@/lib/server-messages';

/** FIN-04 data layer — the receipts one mirrored to money-out. */

export const PAYMENTS_KEY = ['payments'] as const;

export interface PaymentListFilters {
  partnerId?: string | undefined;
  status?: PaymentResponse['status'] | undefined;
  /** RV-01 — the register's search box (number, partner, reference). */
  q?: string | undefined;
}

/** FIN-04's register, one page at a time (NFR-42). Both filters run in SQL. */
export function usePayments(
  filters: PaymentListFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...PAYMENTS_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.payments.list({
        query: {
          ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.q ? { q: filters.q } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function usePayment(paymentId: string | undefined) {
  return useQuery({
    queryKey: [...PAYMENTS_KEY, 'one', paymentId],
    enabled: Boolean(paymentId),
    queryFn: async (): Promise<PaymentResponse> => {
      const response = await api.payments.get({ params: { paymentId: paymentId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * The supplier's open POSTED bills, oldest first (R9, BR-FIN-04-13), ONE PAGE
 * AT A TIME (NFR-42) — the receipt side's twin, `?billPage=` in the URL
 * (PAGINATION.md §3 step 12).
 *
 * Nothing folds over this either: the cash spread is the server's
 * (`useAutoAllocatePreview`), and the SAVE payload is built from the editor's
 * `alloc` and `creditAlloc` rather than from these rows — §3 step 13.
 */
export function useOpenBills(
  partnerId: string | null | undefined,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  /** The voucher's currency (2026-10-30): only bills in it are open to it. Absent = the supplier's. */
  currency?: string | undefined,
) {
  return useQuery({
    queryKey: [...PAYMENTS_KEY, 'open-bills', partnerId, page, pageSize, currency ?? null],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<{
      bills: OpenBill[];
      total: number;
      page: number;
      pageSize: number;
    }> => {
      const response = await api.payments.openBills({
        query: { partnerId: partnerId!, page, pageSize, ...(currency ? { currency } : {}) },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * GEN-50's first read for the credit grid's bill picker: ONE PANEL of the
 * supplier's open bills matching what the reader is typing (`PICKER_PAGE_SIZE`,
 * never the list).
 *
 * It is a separate hook from `useOpenBills` above rather than a parameter on
 * it, because they are two different reads that happen to share a route: the
 * GRID's is a page of the whole open set at `DEFAULT_PAGE_SIZE`, keyed by the
 * `?billPage=` in the URL, and turning its page must not be disturbed by a
 * keystroke in a picker three rows below it. Sharing the hook would put both
 * under one query key and make the grid flicker to twenty rows while somebody
 * typed.
 *
 * There is no matching `held` read (the partner picker's `usePartnersHeld`),
 * and that is the contract's note made good: the bills this screen HOLDS
 * already arrive by other routes — `useBill` for Q-FIN-04-6's carried id, and
 * the voucher's own allocations, which carry their `billCode` — so the
 * picker's `selected` is resolved from those rather than from a second
 * bounded request. See `seenBills` in the editor.
 */
export function useOpenBillSearch(partnerId: string | null | undefined, q: string) {
  return useQuery({
    queryKey: [...PAYMENTS_KEY, 'open-bills', 'search', partnerId, q],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<OpenBill[]> => {
      const response = await api.payments.openBills({
        query: {
          partnerId: partnerId!,
          q: q || undefined,
          page: 1,
          pageSize: PICKER_PAGE_SIZE,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.bills;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * PUR-04 §5 field 8's other half, for the allocation grid: the supplier's
 * POSTED debit notes — the standing credit a payment row may spend instead
 * of cash. A note that has already flipped to `credited`/`refunded` carries
 * no NEW credit and is not offered; a loaded voucher's own credit rows
 * render from its allocations regardless (the stragglers pattern).
 */
export function usePartnerCredits(partnerId: string | null | undefined) {
  return useQuery({
    queryKey: [...PAYMENTS_KEY, 'partner-credits', partnerId],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<PurchaseReturnResponse[]> => {
      const response = await api.purchaseReturns.list({
        query: { partnerId: partnerId!, status: 'posted', page: 1, pageSize: DEFAULT_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.returns;
    },
  });
}

export function useCreatePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePaymentRequest): Promise<PaymentResponse> => {
      const response = await api.payments.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY }),
  });
}

export function useUpdatePayment(paymentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdatePaymentRequest): Promise<PaymentResponse> => {
      const response = await api.payments.update({ params: { paymentId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY }),
  });
}

export function useSetPaymentAllocations(paymentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetPaymentAllocationsRequest): Promise<PaymentResponse> => {
      const response = await api.payments.setAllocations({ params: { paymentId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY }),
  });
}

export function useAutoAllocatePayment(paymentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<PaymentResponse> => {
      const response = await api.payments.autoAllocate({ params: { paymentId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY }),
  });
}

/**
 * The unsaved voucher's auto-allocate — the supplier-side twin of
 * `useAutoAllocatePreview`, and the same reason: the browser folded the
 * supplier's whole open set to answer this, which is what keeps
 * `GET /api/payments/open-bills` unpaged. The spread is CASH only; the
 * screen's note-funded rows spend a debit note's credit and are untouched.
 */
export function useAutoAllocatePaymentPreview() {
  return useMutation({
    mutationFn: async (
      input: PaymentAutoAllocatePreviewRequest,
    ): Promise<PaymentAutoAllocatePreview> => {
      const response = await api.payments.autoAllocatePreview({ body: input });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * The approval action (accounting.approve): Approved stamps the caller and
 * the date; Pending/Rejected clear both (BR-FIN-04-03/-04).
 */
export function useSetPaymentApproval(paymentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (status: 'pending' | 'approved' | 'rejected'): Promise<PaymentResponse> => {
      const response = await api.payments.setApproval({ params: { paymentId }, body: { status } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY }),
  });
}

export function useDeletePayment(paymentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.payments.remove({ params: { paymentId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY }),
  });
}

/** Draft → Posted: bill balances and the order strips move (FIN-04 §12). */
export function usePostPayment(paymentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<PaymentResponse> => {
      const response = await api.payments.post({ params: { paymentId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
      void queryClient.invalidateQueries({ queryKey: BILLS_KEY });
      void queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY });
    },
  });
}

export function useCancelPayment(paymentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    /** GEN-59: the cancel carries its typed reason. */
    mutationFn: async (reason: string): Promise<PaymentResponse> => {
      const response = await api.payments.cancel({ params: { paymentId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY }),
  });
}

/**
 * BR-FIN-04-11 / R8 — the posted-voucher correction: the opposite entry
 * reopens every bill it paid, so bills and orders must refetch exactly as
 * posting makes them.
 */
export function useReversePayment(paymentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<PaymentResponse> => {
      const response = await api.payments.reverse({ params: { paymentId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
      void queryClient.invalidateQueries({ queryKey: BILLS_KEY });
      void queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY });
    },
  });
}
